import * as React from "react";
import { Spinner } from "components/elements/Spinner";
import { type VariantProps, cva } from "lib/cn";

export const button = cva(
    "flex cursor-pointer items-center min-w-max justify-center rounded-md font-medium transition-colors relative focus:outline-hidden focus:ring-0 focus:ring-offset-0",
    {
        variants: {
            size: {
                normal: "h-9 px-4",
                smaller: "h-8 px-3 text-sm",
                smallest: "h-7 px-2 text-sm",
            },
            variant: {
                primary: "bg-brand-atlantic text-white hover:bg-[#0B96D8] active:bg-[#097BB3]",
                secondary:
                    "bg-brand-polarice text-[#424242] hover:bg-brand-shine active:bg-brand-azure",
                outline:
                    "border border-solid border-border text-brand-black2 hover:bg-brand-polarice",
                dotted: "border border-dotted border-[#424242] text-brand-black2",
                hidden: "hover:bg-slate-100 active:bg-slate-200 text-brand-black2",
                clean: "border-0 text-brand-black2",
            },
            disabled: {
                true: "cursor-not-allowed opacity-50",
            },
            color: {
                blue: "",
                red: "",
            },
        },
        compoundVariants: [
            {
                variant: "primary",
                color: "blue",
                className: "bg-brand-atlantic text-white hover:bg-[#0B96D8] active:bg-[#097BB3]",
            },
            {
                variant: "primary",
                color: "red",
                className: "bg-rose-500 text-white hover:bg-rose-600 active:bg-rose-700",
            },
        ],
        defaultVariants: {
            color: "blue",
            disabled: false,
            size: "normal",
            variant: "primary",
        },
    },
);

export type ButtonProps = React.ComponentProps<"button"> &
    VariantProps<typeof button> & { isLoading?: boolean };

export const Button = React.forwardRef(
    (
        { children, className, isLoading, color, disabled, size, variant, ...props }: ButtonProps,
        ref,
    ) => {
        return (
            <button
                ref={ref as any}
                className={button({
                    color,
                    disabled: isLoading || disabled,
                    size,
                    variant,
                    className,
                })}
                disabled={disabled}
                {...props}
            >
                {isLoading && (
                    <span className="absolute inset-0 flex items-center justify-center">
                        <Spinner negativeLogo={true} className="h-6 w-6" />
                    </span>
                )}
                {isLoading ? <span className="visibility-hidden">{children}</span> : children}
            </button>
        );
    },
);
